import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { formatCurrency, formatPercentage, formatDate } from './utils';

// Import jspdf-autotable - this extends jsPDF prototype with autoTable method
// Must be imported as a side effect
import 'jspdf-autotable';

/**
 * Export data to PDF
 */
export const exportToPDF = (options = {}) => {
  const {
    title = 'Report',
    filename = 'report.pdf',
    columns = [],
    tableData = [],
    kpis = [],
    subtitle = '',
    branchName = null
  } = options;

  const doc = new jsPDF();
  
  // Check if autoTable is available (jspdf-autotable should extend jsPDF prototype)
  if (typeof doc.autoTable !== 'function') {
    console.error('jspdf-autotable plugin not loaded. doc.autoTable is:', typeof doc.autoTable);
    throw new Error('PDF export plugin not available. Please ensure jspdf-autotable is properly installed and imported.');
  }
  
  let yPos = 20;

  // Title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, yPos);
  yPos += 10;

  // Subtitle
  if (subtitle) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, 14, yPos);
    yPos += 5;
  }

  // Branch filter info
  if (branchName) {
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.text(`Branch: ${branchName}`, 14, yPos);
    yPos += 5;
  }

  // Date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, 14, yPos);
  yPos += 10;

  // KPIs Section
  if (kpis && kpis.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Key Performance Indicators', 14, yPos);
    yPos += 8;

    // Create KPI table
    const kpiTableData = kpis.map(kpi => [
      kpi.label || '',
      kpi.format === 'currency' ? formatCurrency(kpi.value) :
      kpi.format === 'percentage' ? formatPercentage(kpi.value) :
      kpi.format === 'number' ? kpi.value.toLocaleString() :
      kpi.value || 'N/A'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [['Metric', 'Value']],
      body: kpiTableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // Table Data Section
  if (tableData && tableData.length > 0 && columns && columns.length > 0) {
    // Check if we need a new page
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Detailed Data', 14, yPos);
    yPos += 8;

    // Prepare table data
    const tableHeaders = columns.map(col => col.label || col.key);
    const tableRows = tableData.map(row => 
      columns.map(col => {
        const value = row[col.key];
        if (col.format === 'currency') return formatCurrency(value);
        if (col.format === 'percentage') return formatPercentage(value);
        if (col.format === 'date') return formatDate(value);
        return value?.toString() || 'N/A';
      })
    );

    doc.autoTable({
      startY: yPos,
      head: [tableHeaders],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: 14, right: 14 },
      columnStyles: columns.reduce((acc, col, idx) => {
        if (col.format === 'currency' || col.format === 'number') {
          acc[idx] = { halign: 'right' };
        }
        return acc;
      }, {})
    });
  }

  // Save PDF
  doc.save(filename);
};

/**
 * Export data to Excel (XLSX)
 */
export const exportToExcel = (data, options = {}) => {
  const {
    title = 'Report',
    filename = 'report.xlsx',
    columns = [],
    tableData = [],
    kpis = [],
    branchName = null
  } = options;

  const workbook = XLSX.utils.book_new();

  // Create Summary Sheet with KPIs
  if (kpis && kpis.length > 0) {
    const summaryData = [
      ['Branch Performance Report'],
      [title],
      branchName ? [`Branch: ${branchName}`] : [],
      [`Generated: ${new Date().toLocaleDateString('en-GB')}`],
      [],
      ['Key Performance Indicators'],
      ['Metric', 'Value']
    ];

    kpis.forEach(kpi => {
      const value = kpi.format === 'currency' ? formatCurrency(kpi.value) :
                   kpi.format === 'percentage' ? formatPercentage(kpi.value) :
                   kpi.format === 'number' ? kpi.value.toLocaleString() :
                   kpi.value || 'N/A';
      summaryData.push([kpi.label || '', value]);
    });

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');
  }

  // Create Data Sheet
  if (tableData && tableData.length > 0 && columns && columns.length > 0) {
    const headers = columns.map(col => col.label || col.key);
    const rows = tableData.map(row => 
      columns.map(col => {
        const value = row[col.key];
        // For Excel, keep numeric values as numbers
        if (col.format === 'currency' || col.format === 'number') {
          return typeof value === 'number' ? value : parseFloat(value) || 0;
        }
        return value?.toString() || '';
      })
    );

    const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    // Set column widths
    const colWidths = columns.map(() => ({ wch: 15 }));
    dataSheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, dataSheet, 'Data');
  }

  // Save Excel file
  XLSX.writeFile(workbook, filename);
};

/**
 * Export data to CSV
 */
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Prepare email data (returns formatted data for email API)
 */
export const prepareEmailData = (data, options = {}) => {
  const {
    title = 'Report',
    recipientEmail = '',
    subject = '',
    columns = [],
    tableData = [],
    kpis = [],
    branchName = null
  } = options;

  // Format data for email
  let emailBody = `<h2>${title}</h2>`;
  
  if (branchName) {
    emailBody += `<p><strong>Branch:</strong> ${branchName}</p>`;
  }
  
  emailBody += `<p><strong>Generated:</strong> ${new Date().toLocaleDateString('en-GB')}</p>`;
  emailBody += '<hr>';

  // KPIs
  if (kpis && kpis.length > 0) {
    emailBody += '<h3>Key Performance Indicators</h3>';
    emailBody += '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse;">';
    emailBody += '<tr><th>Metric</th><th>Value</th></tr>';
    kpis.forEach(kpi => {
      const value = kpi.format === 'currency' ? formatCurrency(kpi.value) :
                   kpi.format === 'percentage' ? formatPercentage(kpi.value) :
                   kpi.format === 'number' ? kpi.value.toLocaleString() :
                   kpi.value || 'N/A';
      emailBody += `<tr><td>${kpi.label || ''}</td><td>${value}</td></tr>`;
    });
    emailBody += '</table><br>';
  }

  // Table Data
  if (tableData && tableData.length > 0 && columns && columns.length > 0) {
    emailBody += '<h3>Detailed Data</h3>';
    emailBody += '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse: collapse;">';
    emailBody += '<tr>' + columns.map(col => `<th>${col.label || col.key}</th>`).join('') + '</tr>';
    
    tableData.forEach(row => {
      emailBody += '<tr>' + columns.map(col => {
        const value = row[col.key];
        const formatted = col.format === 'currency' ? formatCurrency(value) :
                         col.format === 'percentage' ? formatPercentage(value) :
                         col.format === 'date' ? formatDate(value) :
                         value?.toString() || 'N/A';
        return `<td>${formatted}</td>`;
      }).join('') + '</tr>';
    });
    
    emailBody += '</table>';
  }

  return {
    to: recipientEmail,
    subject: subject || `${title} - ${new Date().toLocaleDateString('en-GB')}`,
    body: emailBody,
    html: true
  };
};

